/**
 * TokenRefreshQueue — coordinador singleton del refresh de token.
 *
 * Reemplaza dos mecanismos previos que podían quedar desincronizados:
 *   - `apiClient.isRefreshing` + `refreshSubscribers[]` (local a ApiClient)
 *   - `authService.refreshPromise` (mutex módulo-global)
 *
 * Cuando varios requests reciben 401 simultáneamente, todos pasan por
 * `tokenRefreshQueue.refresh(...)` y comparten el mismo fetch a
 * `/auth/refresh` (dedup natural). Si ese fetch cuelga —antes no tenía
 * timeout— esta queue lo rechaza a los 10s y drena a los waiting con
 * `RefreshTimeoutError`, evitando el bug de "shimmer eterno" donde los
 * subscribers quedaban en limbo esperando una promesa muerta.
 */

const REFRESH_TIMEOUT_MS = 10_000;

export class RefreshTimeoutError extends Error {
  readonly kind = 'timeout' as const;
  constructor(message = 'Token refresh timed out') {
    super(message);
    this.name = 'RefreshTimeoutError';
  }
}

class TokenRefreshQueue {
  /** Promise del refresh en curso. Null si no hay ninguno. */
  private currentPromise: Promise<void> | null = null;

  /**
   * Dedup: si ya hay un refresh en curso devuelve la misma promise.
   * Si no, lanza uno nuevo con timeout hard.
   */
  refresh(runner: () => Promise<void>): Promise<void> {
    if (this.currentPromise) return this.currentPromise;
    this.currentPromise = this.runWithTimeout(runner).finally(() => {
      this.currentPromise = null;
    });
    return this.currentPromise;
  }

  private runWithTimeout(runner: () => Promise<void>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new RefreshTimeoutError());
      }, REFRESH_TIMEOUT_MS);
      runner()
        .then(() => {
          clearTimeout(timer);
          resolve();
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }
}

export const tokenRefreshQueue = new TokenRefreshQueue();
